import React, { useState } from 'react';
import { Typography, Box, Grid, Card, CardContent, Button, Chip } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import {
  fetchCarsByBrand, setSelectedBrand, fetchCarsByBodyType, fetchCarUsage, fetchCarsByPrice,
  fetchCarsByMileage, fetchCarsByEngine, fetchCarsByOtherSpecs, fetchCarsByColor, fetchAllCarModels
} from './store';
import MoreFilterDialog from './moreFilter';
import "./advanceFilter.css";

const AdvanceFilter = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const iconVariants = {
    hover: {
      scale: 1.1,
      rotate: [0, -5, 5, 0],
      transition: { duration: 0.3 },
    }
  };

  // Handlers (same as before)
  const handleBrandClick = (brand) => {
    dispatch(setSelectedBrand(brand));
    dispatch(fetchCarsByBrand(brand))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?brandName=${brand}`);
      });
  };

  const handleCarUsageClick = (usageFilter) => {
    dispatch(fetchCarUsage(usageFilter))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?carUsage=${encodeURIComponent(usageFilter)}`);
      });
  };

  const handleBodyTypeClick = (bodyType) => {
    dispatch(fetchCarsByBodyType(bodyType))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?bodyType=${bodyType}`);
      });
  };

  const handlePriceClick = (priceFilter) => {
    dispatch(fetchCarsByPrice(priceFilter))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?price=${encodeURIComponent(priceFilter)}`);
      });
  };

  const handleMileageClick = (mileageFilter) => {
    dispatch(fetchCarsByMileage(mileageFilter))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?mileage=${encodeURIComponent(mileageFilter)}`);
      });
  };

  const handleEngineClick = (engineFilter) => {
    dispatch(fetchCarsByEngine(engineFilter))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?engine=${encodeURIComponent(engineFilter)}`);
      });
  };

  const handleOtherSpecsClick = (specType, specValue) => {
    dispatch(fetchCarsByOtherSpecs({ type: specType, value: specValue }))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?specType=${encodeURIComponent(specType)}&specValue=${encodeURIComponent(specValue)}`);
      });
  };

  const handleColorClick = (colorName) => {
    dispatch(fetchCarsByColor(colorName))
      .then((result) => {
        if (result) navigate(`/exploreByGlobalSearch?color=${encodeURIComponent(colorName)}`);
      });
  };

  // Data arrays - Original values with display labels
  const bodyTypes = [
    { id: 1, name: 'SEDAN', icon: '/BrandIcons/body1.svg' },
    { id: 2, name: 'SUV', icon: '/BrandIcons/body2.svg' },
    { id: 3, name: 'HATCHBACK', icon: '/BrandIcons/body3.svg' },
    { id: 4, name: 'MINI_VAN', icon: '/BrandIcons/body4.svg' },
    { id: 5, name: 'OPEN', icon: '/BrandIcons/body5.svg' },
    { id: 6, name: 'COUPE', icon: '/BrandIcons/body6.svg' },
  ];

  // Original values with display labels for compact view
  const filterOptions = [
    { value: 'Less than 1 Year', display: '< 1 Yr' },
    { value: '1 to 3 Years Old', display: '1-3 Yrs' },
    { value: '4 to 5 Years Old', display: '4-5 Yrs' },
    { value: '6 to 8 Years Old', display: '6-8 Yrs' },
    { value: '9 to 12 Years Old', display: '9-12 Yrs' },
    { value: '13 + Years Old', display: '13+ Yrs' },
    { value: '25 + Years Old', display: '25+ Yrs' }
  ];

  // const priceOptions = [
  //   { value: 'Under US $1,000', display: '< $1K' },
  //   { value: 'US $1,001-$3,000', display: '$1-3K' },
  //   { value: 'US $3,001-$5,000', display: '$3-5K' },
  //   { value: 'US $5,001-$7,000', display: '$5-7K' },
  //   { value: 'US $7,001-$10,000', display: '$7-10K' },
  //   { value: 'US $10,001-$15,000', display: '$10-15K' }
  // ];
//   const priceOptions = [
//   { value: '$0-$1000', display: '$0-1K' },
//   { value: '$1001-$2000', display: '$1-2K' },
//   { value: '$2001-$3000', display: '$2-3K' },
//   { value: '$3001-$4000', display: '$3-4K' },
//   { value: '$4001-$5000', display: '$4-5K' },
//   { value: '$5001-$6000', display: '$5-6K' },
//   { value: '$6001-$7000', display: '$6-7K' },
//   { value: '$7001-$8000', display: '$7-8K' },
//   { value: '$8001-$9000', display: '$8-9K' },
//   { value: '$9001-$10000', display: '$9-10K' },
//   { value: '$10001_ABOVE', display: '$10K+' },
// ];
const priceOptions = [
    { value: '$0-$1000', display: '$0-1K' },
    { value: '$1001-$2000', display: '$1-2K' },
    { value: '$2001-$3000', display: '$2-3K' },
    { value: '$3001-$4000', display: '$3-4K' },
    { value: '$4001-$5000', display: '$4-5K' },
    { value: '$5001-$6000', display: '$5-6K' },
    { value: '$6001_ABOVE', display: '$6K_Above' },  // ✅ Ye ek button cover karega $6K+
  ];

  const mileageOptions = [
    { value: 'Less than 5,000', display: ' 0-5K km' },
    { value: '5k-10k KM', display: ' 5-10K km' },
    { value: '10k-20k KM', display: ' 10-20K km' },
    { value: '20k-30k KM', display: ' 20-30K km' },
    { value: '30k-40k KM', display: ' 30-40k km' },
    { value: '40k-50k KM', display: ' 40-50k km' },
    { value: '50k KM AND ABOVE', display: ' 50k above' }

  ];

  // const engineOptions = [
  //   { value: 'underSevenHundredCC', display: '< 700 cc' },
  //   { value: 'sevenHundredCCToOneThousandCC', display: '700-1K cc' },
  //   { value: 'oneThousandCCToOneThousandFiveHundredCC', display: '1-1.5K cc' },
  //   { value: 'oneThousandFiveHundredCCToTwoThousandCC', display: '1.5-2K cc' },
  //   { value: 'twoThousandCCToThreeThousandCC', display: '2-3K cc' },
  //   { value: 'threeThousandCCToSixThousandCC', display: '3-6K cc' }
  // ];
  const engineOptions = [
  { value: '0CC-500CC', display: '0-500 cc' },
  { value: '501CC-1000CC', display: '501-1K cc' },
  { value: '1001CC-1500CC', display: '1-1.5K cc' },
  { value: '1501CC-2000CC', display: '1.5-2K cc' },
  { value: '2001CC-2500CC', display: '2-2.5K cc' },
  { value: '2501CC-3000CC', display: '2.5-3K cc' },
  { value: '3001CC_ABOVE', display: '3K+ cc' },
];

  const otherSpecs = [
    { label: 'Transmission', options: [{ value: 'at', displayValue: 'AUTOMATIC' }, { value: 'mt', displayValue: 'MANUAL' }] },
    { label: 'Drive', options: [{ value: 'twoWD', displayValue: '2WD' }, { value: 'fourWD', displayValue: '4WD' }] },
    { label: 'Fuel', options: [{ value: 'petrol', displayValue: 'PETROL' }, { value: 'diesel', displayValue: 'DIESEL' }, { value: 'hybrid', displayValue: 'HYBRID' }, { value: 'electric', displayValue: 'ELECTRIC' }] },
    { label: 'Steering', options: [{ value: 'left', displayValue: 'Left Drive' }, { value: 'right', displayValue: 'Right Drive' }] }
  ];

  const colorMapping = {
    circle1: { name: "WHITE", hex: "#FFFFFF", border: "1px solid #ccc" },
    circle2: { name: "PERL", hex: "#EAE0C8" },
    circle3: { name: "BEIGE", hex: "#F5F5DC" },
    circle4: { name: "SILVER", hex: "#C0C0C0" },
    circle5: { name: "GRAY", hex: "#808080" },
    circle6: { name: "GUNMETAL", hex: "#2A3439" },
    circle7: { name: "BLACK", hex: "#000000" },
    circle8: { name: "PINK", hex: "#FFC0CB" },
    circle9: { name: "PURPLE", hex: "#800080" },
    circle10: { name: "WINE_RED", hex: "#722F37" },
    circle11: { name: "RED", hex: "#FF0000" },
    circle12: { name: "YELLOW", hex: "#FFFF00" },
    circle13: { name: "ORANGE", hex: "#FFA500" },
    circle14: { name: "GOLD", hex: "#FFD700" },
    circle15: { name: "BROWN", hex: "#964B00" },
    circle16: { name: "LIGHT_BLUE", hex: "#ADD8E6" },
  };

  const brandsRow1 = [
    { name: "NISSAN", icon: "/BrandIcons/Brand_Nissan.svg", handler: "NISSAN" },
    { name: "HONDA", icon: "/BrandIcons/Brand_Honda.svg", handler: "HONDA" },
    // { name: "SEAT", icon: "/BrandIcons/Brand_Seat.svg", handler: "SEAT" },
    { name: "TOYOTA", icon: "BrandIcons/Brand_Toyota.svg", handler: "TOYOTA" },
    { name: "BMW", icon: "/BrandIcons/Brand_BMW.svg", handler: "BMW" },
  ];

  const brandsRow2 = [
    { name: "MAZDA", icon: "/BrandIcons/Brand_MAZDA.svg", handler: "MAZDA" },
    // { name: "MINI", icon: "/BrandIcons/Brand_Mini.svg", handler: "MINI" },
    { name: "FIAT", icon: "/BrandIcons/Brand_Fiat.svg", handler: "FIAT" },
    { name: "PORSCHE", icon: "/BrandIcons/Brand_Porsche.svg", handler: "PORSCHE" },
    { name: "RENAULT", icon: "/BrandIcons/Brand_Renault.svg", handler: "RENAULT" },
  ];

  return (
    <div className="main-filter">
      {/* Compact Header */}
      <motion.div
        className="advance-filter-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" className="header-title-advance-filter">
          <span className="highlight">Explore</span> by Filters
        </Typography>
        <div className="header-underline" />
        <motion.div
          className="header-description-wrapper"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box className="header-description-box">
            <Typography className="header-subtitle-main">
              🚗 Your Perfect Car Awaits
            </Typography>
            <Typography className="header-subtitle-detail">
              Discover your ideal vehicle with precision filtering. Browse by <span className="highlight-text">Brand</span>,
              <span className="highlight-text"> Body Type</span>, <span className="highlight-text">Year</span>,
              <span className="highlight-text"> Price Range</span>, <span className="highlight-text">Mileage</span>,
              <span className="highlight-text"> Engine Size</span>, <span className="highlight-text">Fuel Type</span>,
              <span className="highlight-text"> Transmission</span>, and more.
              <strong> Find exactly what you need, faster than ever!</strong>
            </Typography>
          </Box>
        </motion.div>
      </motion.div>

      {/* Main Content - Single View Layout */}
      <Box className="single-view-container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={1.5} className="main-grid">

            {/* Top Section - Brands & Body Types */}
            <Grid item xs={12}>
              <Grid container spacing={1.5}>

                {/* Brands - Compact Card */}
                <Grid item xs={12} md={6}>
                  <motion.div variants={itemVariants}>
                    <Card className="modern-card brands-card">
                      <CardContent className="card-content-compact">
                        <div className="card-header-advance-filter">
                          <Typography className="card-title">Popular Brands</Typography>
                        </div>
                        <div className="brands-compact-grid">
                          {[...brandsRow1, ...brandsRow2].map((brand) => (
                            <motion.div
                              key={brand.name}
                              className="brand-item-compact"
                              variants={iconVariants}
                              whileHover="hover"
                              onClick={() => handleBrandClick(brand.handler)}
                            >
                              <img src={brand.icon} alt={brand.name} className="brand-icon-compact" />
                              <span className="brand-label">{brand.name}</span>
                            </motion.div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                {/* Body Types - Compact Card */}
                <Grid item xs={12} md={6}>
                  <motion.div variants={itemVariants}>
                    <Card className="modern-card body-types-card">
                      <CardContent className="card-content-compact">
                        <div className="card-header-advance-filter">
                          <Typography className="card-title">Body Types</Typography>
                        </div>
                        <div className="body-types-grid">
                          {bodyTypes.map((bodyType) => (
                            <motion.div
                              key={bodyType.id}
                              className="body-type-compact"
                              variants={iconVariants}
                              whileHover="hover"
                              onClick={() => handleBodyTypeClick(bodyType.name)}
                            >
                              <img src={bodyType.icon} alt={bodyType.name} className="body-icon" />
                              <span className="body-label">{bodyType.name}</span>
                            </motion.div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </Grid>

            {/* Middle Section - All Filters in Same Row with Equal Width */}
            <Grid item xs={12}>
              <motion.div variants={itemVariants}>
                <Card className="modern-card filters-card-advance-filter">
                  <CardContent className="card-content-compact">
                    <Grid container spacing={1.5} className="equal-width-container-advance-filter">

                      {/* Car Usage */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Usage</Typography>
                          <div className="filter-chips-advance-filter">
                            {filterOptions.map((filter) => (
                              <Chip
                                key={filter.value}
                                label={filter.display}
                                className="filter-chip-advance-filter"
                                onClick={() => handleCarUsageClick(filter.value)}
                              />
                            ))}
                          </div>
                        </div>
                      </Grid>

                      {/* Price */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Price</Typography>
                          <div className="filter-chips-advance-filter">
                            {priceOptions.map((price) => (
                              <Chip
                                key={price.value}
                                label={price.display}
                                className="filter-chip-advance-filter"
                                onClick={() => handlePriceClick(price.value)}
                              />
                            ))}
                          </div>
                        </div>
                      </Grid>

                      {/* Mileage */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Mileage</Typography>
                          <div className="filter-chips-advance-filter">
                            {mileageOptions.map((mileage) => (
                              <Chip
                                key={mileage.value}
                                label={mileage.display}
                                className="filter-chip-advance-filter"
                                onClick={() => handleMileageClick(mileage.value)}
                              />
                            ))}
                          </div>
                        </div>
                      </Grid>

                      {/* Engine */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Engine</Typography>
                          <div className="filter-chips-advance-filter">
                            {engineOptions.map((engine) => (
                              <Chip
                                key={engine.value}
                                label={engine.display}
                                className="filter-chip-advance-filter"
                                onClick={() => handleEngineClick(engine.value)}
                              />
                            ))}
                          </div>
                        </div>
                      </Grid>

                      {/* Quick Specs - Equal Width */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Quick Specs</Typography>
                          <div style={{marginTop:"10px"}} className="specs-chips-full">
                            {/* {otherSpecs.map((spec) => (
                              <div key={spec.label} className="spec-group-full">
                                <div className="spec-options-full">
                                  {spec.options.map((option) => (
                                    <Chip
                                      key={option.value}
                                      label={option.displayValue}
                                      size="small"
                                      className="spec-chip-full"
                                      onClick={() => handleOtherSpecsClick(spec.label.toLowerCase(), option.value)}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))} */}
                            {otherSpecs.map((spec) => (
                              <div key={spec.label} className="spec-group-full">
                                {/* <Typography className="spec-group-label">{spec.label}</Typography> */}
                                <div className="spec-options-full">
                                  {spec.options.map((option) => (
                                    <Chip
                                      key={option.value}
                                      label={option.displayValue}
                                      size="small"
                                      className="spec-chip-full"
                                      onClick={() => handleOtherSpecsClick(spec.label.toLowerCase(), option.value)}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Grid>

                      {/* Colors - Equal Width */}
                      <Grid item xs={6} sm={4} md={2} className="filter-column-equal-advance-filter">
                        <div className="filter-column-advance-filter">
                          <Typography className="filter-column-title">Colors</Typography>
                          <div className="colors-full">
                            <div className="colors-grid-full">
                              {Object.entries(colorMapping).map(([key, { name, hex, border }]) => (
                                <motion.div
                                  key={key}
                                  className="color-item-full"
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleColorClick(name)}
                                >
                                  <div
                                    className="color-circle-full"
                                    style={{
                                      backgroundColor: hex,
                                      border: border || 'none'
                                    }}
                                    title={name}
                                  />
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Bottom Section - Only Advanced Filter Button */}
            <Grid item xs={12} sx={{paddingBottom:"40px"}}>
              <Grid container spacing={1.5} justifyContent="center">
                <Grid item xs={12} md={3}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      fullWidth
                      size="medium"
                      variant="contained"
                      className="advance-filter-btn-new"
                      onClick={handleOpenDialog}
                    >
                      <span className="btn-icon-new">⚡</span>
                      Advanced Filter
                    </Button>
                  </motion.div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* More Filter Dialog */}
      <MoreFilterDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onApplyFilters={(filters) => {
          console.log("Applied Filters:", filters);
          setOpenDialog(false);
        }}
      />
    </div>
  );
};

export default AdvanceFilter;